'use client'
import {
  BackIcon,
  ClearIcon,
  SearchIcon,
} from '@/app/[locale]/_assets/icon-components'
import NumberToPrice from '@/app/[locale]/_components/NumberToPrice'
import useDebounce from '@/app/[locale]/_hooks/useDebounce'
import { useProductListInfinite } from '@/app/[locale]/_service/product.service'
import { IRedux } from '@/app/[locale]/_store/types'
import { useProductCartRedirect } from '@/app/[locale]/_hooks/useProductCartRedirect'
import {
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogRoot,
} from '@/components/ui/dialog'
import { InputGroup } from '@/components/ui/input-group'
import {
  Button,
  Center,
  Flex,
  HStack,
  IconButton,
  Input,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react'
import { useTranslations } from 'next-intl'
import { Dispatch, useEffect, useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { shallowEqual, useSelector } from 'react-redux'
import ProductCard from '../../../Card'
import Loading from './Loading'
import { useDeliveryPrice } from '@/app/[locale]/_hooks/useDeliveryPrice'
import { getCookie } from 'cookies-next/client'
import { getOrderingDeliveryType } from '@/app/[locale]/_utils/modifier/buildCompulsoryModifiers'

const SearchMobile = ({
  open = false,
  setOpen,
}: {
  open?: boolean
  setOpen?: Dispatch<boolean>
}) => {
  const [search, setSearch] = useState<string>('')
  const [totalPrice, setTotalPrice] = useState(0)

  const { branch, session, cart, settings, auth } = useSelector(
    (state: IRedux) => ({
      branch: state.common.branch,
      session: state.session,
      cart: state.cart.cart,
      settings: state.settings,
      auth: state.auth,
    }),
    shallowEqual
  )

  const source_id: string | undefined = useMemo(() => {
    return (
      session.source_id ??
      auth.miniAppCrmData?.source_id ??
      getCookie('source_id')
    )
  }, [session, auth])
  const { menu_id, is_hall, order_type } = session
  const isDelivery = order_type?.type === 'delivery'
  const deliveryType = useMemo(
    () =>
      getOrderingDeliveryType({
        isHall: is_hall === 'true',
        orderType: order_type?.type,
      }),
    [is_hall, order_type?.type]
  )
  const showDeliveryCost =
    isDelivery && settings?.data?.data?.is_home_page_delivery
  const { deliveryPrice } = useDeliveryPrice(totalPrice, {
    isHomePage: true,
    enabled: showDeliveryCost,
  })
  const totalWithDelivery = totalPrice + (showDeliveryCost ? deliveryPrice : 0)

  const debounceSearch = useDebounce(search, 200)
  const t = useTranslations()
  const redirectToCart = useProductCartRedirect(() => setOpen?.(false))

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useProductListInfinite({
      params: {
        page: 1,
        limit: 20,
        search: debounceSearch,
        fields:
          'slug,image,categories,description,google_category,tags,discounts',
        product_types: 'simple,combo,origin',
        menu_id:
          is_hall === 'true'
            ? menu_id !== 'null'
              ? menu_id
              : ''
            : branch?.menu_id || '',
        aggregator_id: source_id,
        delivery_type: deliveryType,
      },
      props: {
        enabled: open,
      },
    })

  const products = data?.pages?.flatMap((p) => p?.products ?? []) ?? []

  useEffect(() => {
    if (cart.length > 0) {
      let total = 0
      cart?.map((product) => {
        total += (product.price_with_discount || 0) * product.quantity
        if (product?.order_modifiers?.length > 0) {
          for (const modifier of product.order_modifiers) {
            total +=
              +modifier.modifier_price *
              +(modifier?.modifier_quantity * product.quantity)
          }
        }
      })
      setTotalPrice(total)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart])

  useEffect(() => {
    if (products?.length > 0) {
      const allPrice =
        products?.reduce((acc, cur) => acc + cur?.out_price, 0) || 0
      if (typeof dataLayer !== 'undefined') {
        dataLayer?.push({ items: null })
        dataLayer?.push({ value: null })
        dataLayer?.push({ content_ids: null })

        dataLayer?.push({
          event: 'view_search_results_ads',
          value: allPrice,
          items: products?.map((el) => ({
            id: el.id,
            google_business_vertical: 'retail',
          })),
        })

        dataLayer?.push({
          event: 'view_item_results_fb',
          value: allPrice,
          content_ids: products?.map((el) => el.id),
          content_type: 'product',
          currency: settings?.country?.currency || 'UZS',
        })
      }
    }
  }, [products, search])

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
  })

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <DialogRoot
      size={'full'}
      lazyMount
      motionPreset={'slide-in-left'}
      open={open}
      onOpenChange={(e) => setOpen?.(e.open)}
    >
      <DialogContent>
        <DialogHeader px={4}>
          <HStack gap={2}>
            <IconButton
              onClick={() => setOpen?.(false)}
              size={'xl'}
              rounded={16}
              variant={'subtle'}
              bg='background'
              color='text'
            >
              <BackIcon color='currentColor' />
            </IconButton>
            <InputGroup
              flex='1'
              endElement={
                search && (
                  <IconButton
                    variant={'ghost'}
                    _hover={{ bg: 'transparent' }}
                    onClick={() => setSearch('')}
                  >
                    <ClearIcon
                      color='var(--bg-card)'
                      innerColor='var(--text-default)'
                    />
                  </IconButton>
                )
              }
              color='text'
              startElement={<SearchIcon />}
            >
              <Input
                rounded={16}
                outline={'none'}
                size={'xl'}
                variant={'subtle'}
                bg='background'
                color='text'
                _placeholder={{
                  color: 'text.muted',
                }}
                placeholder={t('quick_search')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </InputGroup>
          </HStack>
        </DialogHeader>
        <DialogBody px={4}>
          {isLoading ? (
            <Center h={'full'}>
              <Loading />
            </Center>
          ) : products?.length > 0 ? (
            <div>
              <SimpleGrid
                gap={{ base: 2, lg: 4 }}
                columns={{ base: 2, md: 3, lg: 4 }}
              >
                {products?.map((el, idx) => {
                  const isPreLast = idx === products.length - 2
                  return (
                    <div
                      key={`${el?.id}-${idx}`}
                      ref={isPreLast ? loadMoreRef : undefined}
                    >
                      <ProductCard categoryName='' product={el} />
                    </div>
                  )
                })}
              </SimpleGrid>

              {isFetchingNextPage && (
                <Center py='4'>
                  <Spinner size='lg' color='primary' />
                </Center>
              )}
            </div>
          ) : (
            <Center h={'30vh'}>
              <Text fontSize={'2xl'} fontWeight={700}>
                {t('nothing_was_found')}
              </Text>
            </Center>
          )}
        </DialogBody>
        <Stack
          position='sticky'
          bottom={0}
          left={0}
          bg='background.card'
          color='text'
          zIndex={10}
          w='full'
          pt={4}
          gap={4}
          pb={4}
          px={4}
          transform={`translateY(${cart.length > 0 ? 0 : 100}%)`}
          borderTop='1px solid #00000014'
          boxShadow={{
            base: '0px -4px 6px -2px rgba(0, 0, 0, 0.08)',
            lg: 'none',
          }}
          transition='150ms ease-in-out'
        >
          <Flex flexDirection='column' gap={2}>
            {isDelivery && (
              <>
                <HStack
                  justifyContent='space-between'
                  w='full'
                  fontSize='sm'
                  fontWeight={500}
                >
                  <Text color='text'>{t('order_sum')}</Text>
                  <NumberToPrice
                    value={totalPrice}
                    textProps={{
                      fontWeight: 600,
                      fontSize: 'sm',
                      color: 'text',
                    }}
                    spanProps={{
                      fontWeight: 600,
                      fontSize: 'sm',
                      color: 'text',
                    }}
                  />
                </HStack>
                {showDeliveryCost && (
                  <HStack
                    justifyContent='space-between'
                    w='full'
                    fontSize='sm'
                    fontWeight={500}
                  >
                    <Text color='text'>{t('delivery_cost')}</Text>
                    {deliveryPrice === 0 ? (
                      <Text fontWeight={600} fontSize='sm' color='text'>
                        {t('free')}
                      </Text>
                    ) : (
                      <HStack
                        gap={1}
                        as='span'
                        display='inline-flex'
                        whiteSpace='nowrap'
                      >
                        <Text as='span' color='text' fontWeight={600}>
                          +
                        </Text>
                        <NumberToPrice
                          value={deliveryPrice}
                          textProps={{
                            fontWeight: 600,
                            fontSize: 'sm',
                            color: 'text',
                          }}
                          spanProps={{
                            fontWeight: 600,
                            fontSize: 'sm',
                            color: 'text',
                          }}
                        />
                      </HStack>
                    )}
                  </HStack>
                )}
              </>
            )}
            <HStack
              w='full'
              justifyContent='space-between'
              fontSize='sm'
              fontWeight={600}
            >
              <Text fontSize='sm' fontWeight={500} flex={1} lineClamp={1}>
                {t('price')}
              </Text>
              <NumberToPrice
                value={totalWithDelivery}
                textProps={{ fontWeight: 600, fontSize: 'sm', color: 'text' }}
                spanProps={{ fontWeight: 600, fontSize: 'sm', color: 'text' }}
              />
            </HStack>
          </Flex>
          <Button
            bg='primary'
            color='primary.text'
            _hover={{
              bg: 'primary.hover',
            }}
            rounded={16}
            fontWeight={600}
            display='flex'
            fontSize='sm'
            height='52px'
            onClick={redirectToCart}
          >
            {t('go_to_cart')}
          </Button>
        </Stack>
      </DialogContent>
    </DialogRoot>
  )
}

export default SearchMobile
